import * as React from "react";

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../tooltip";

export interface IconTooltipProps {
  /** The tooltip text — the icon button's name (e.g. "Filter", "Board"). */
  label: React.ReactNode;
  /** Which side the bubble points from. Defaults to above the control. */
  side?: "top" | "right" | "bottom" | "left";
  /** Hover delay before the bubble shows, ms. */
  delayDuration?: number;
  /**
   * The trigger — a single icon `<Button>` (or a `<PopoverTrigger asChild>` /
   * `<DropdownMenuTrigger asChild>` wrapping one). Rendered `asChild`, so no
   * extra DOM: the tooltip merges onto whatever element you pass.
   */
  children: React.ReactElement;
}

/**
 * IconTooltip — wrap ANY icon button so it always carries a real, styled tooltip
 * (Matt, 2026-07-16). An icon with no visible label is unreadable without one, so
 * this is the standing rule for every icon-only control: never rely on the native
 * `title=` attribute (unstyled, slow, inconsistent) — reach for this.
 *
 * Self-contained: it brings its own `TooltipProvider`, so it works anywhere without
 * an ambient provider. Composes with a menu/popover trigger — the order is
 * Tooltip ▸ Popover ▸ Button, all `asChild`, collapsing onto one element:
 *
 *   <IconTooltip label="Filter">
 *     <PopoverTrigger asChild>
 *       <Button variant="ghost" size="icon" aria-label="Filter"><Filter /></Button>
 *     </PopoverTrigger>
 *   </IconTooltip>
 */
export function IconTooltip({
  label,
  side = "top",
  delayDuration = 300,
  children,
}: IconTooltipProps) {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side}>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
